import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionIcon, Alert, Badge, Card, Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle, IconCalendar, IconDownload, IconTrash } from '@tabler/icons-react';
import UploadZone from '@/components/app/company/UploadZone';
import SignedBurst from '@/components/common/SignedBurst';
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_MB, validateUploadFiles } from '@/lib/validateUploadFiles';
import type { DocFile, DocSignStatus, DocType, DocumentGroup } from '@/types/company';

// Лимит размера файла на клиенте — 30 МБ, как в оригинальном портале (frontend-mfe). Внимание:
// очень крупные файлы может отбить шлюз/бэкенд (503/502, см. диагностику загрузки) — тогда
// пользователь увидит понятное «сервис временно недоступен». Радикальное лечение — на бэкенде
// (стриминг multipart в S3 / память пода / 413 на шлюзе).

const STATUS: Record<DocSignStatus, { color: string; key: string } | null> = {
  IsSigned: { color: 'lime', key: 'app.company.accreditation.status.signed' },
  IsNotSigned: { color: 'wheat', key: 'app.company.accreditation.status.notSigned' },
  IsWrongTemplate: { color: 'red', key: 'app.company.accreditation.status.wrongTemplate' },
  IsWrongSign: { color: 'red', key: 'app.company.accreditation.status.wrongSign' },
  IsNotUsed: null,
};

// Итоговые (подписанные обеими сторонами) документы приходят с docType SIGNED_* и
// docSignStatus=IsNotUsed — у такого статуса обычный бейдж по STATUS пуст. Для них показываем явный
// бейдж завершения, одинаковый по виду (lime, light, sm) и месту (строка файла): подписанная анкета —
// «Анкета принята», подписанный агентский договор — «Подписан». Шаблоны на подпись
// (VERIFIED_PROFILE_TEMPLATE / AGENCY_AGREEMENT_TEMPLATE) сохраняют обычный бейдж статуса подписи.
const SIGNED_RESULT_BADGE: Partial<Record<DocType, { color: string; key: string }>> = {
  SIGNED_VERIFIED_PROFILE: { color: 'lime', key: 'app.company.accreditation.status.accepted' },
  SIGNED_AGENCY_AGREEMENT: { color: 'lime', key: 'app.company.accreditation.status.signed' },
};

// Карточка «Агентский договор» (шаблон на подпись): договор двусторонний — встречную подпись
// оператор ДАТА1861 ставит вручную в ЭДО, поэтому её нужно явно запросить у контрагента. Выносим это
// заметным напоминанием (wheat-акцент «внимание»), чтобы шаг не потерялся.
const AGENCY_TEMPLATE_DOC_TYPE: DocType = 'AGENCY_AGREEMENT_TEMPLATE';

// Бейдж статуса документа + (в момент подписания) пилюле-кольцо-пинг вокруг него. Ref нужен
// SignedBurst для замера позиции бейджа — сам всплеск рисуется порталом поверх страницы, чтобы его
// не обрезал overflow:hidden карточки.
function DocBadge({ color, label, burst }: { readonly color: string; readonly label: string; readonly burst: boolean }) {
  const badgeRef = useRef<HTMLDivElement>(null);
  return (
    <>
      <Badge ref={badgeRef} color={color} variant="light" size="sm">
        {label}
      </Badge>
      {burst ? <SignedBurst targetRef={badgeRef} /> : null}
    </>
  );
}

interface DocumentGroupCardProps {
  readonly group: DocumentGroup;
  readonly uploadPending?: boolean;
  /** Процент отправки на сервер (0..100) для этой группы. */
  readonly uploadPercent?: number;
  /** Группа ТОЛЬКО ЧТО стала подписанной (итоговый SIGNED_*) — один раз проигрываем всплеск у бейджа. */
  readonly justSigned?: boolean;
  readonly onDownload: (docType: DocType, docId: number) => void;
  readonly onUpload: (docType: DocType, files: File[]) => void;
  readonly onDelete: (docId: number) => void;
  readonly onSetExpiry: (file: DocFile) => void;
}

// Одна группа документов: заголовок + файлы (статус/скачать/удалить/срок) + загрузка.
// Общая для Аккредитации (G3) и Агентского договора (G4).
export default function DocumentGroupCard({
  group,
  uploadPending,
  uploadPercent,
  justSigned,
  onDownload,
  onUpload,
  onDelete,
  onSetExpiry,
}: DocumentGroupCardProps) {
  const { t } = useTranslation();
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);

  async function handleFiles(files: File[]): Promise<void> {
    setUploadError(undefined);
    const result = await validateUploadFiles(files, {
      extensions: group.extensions,
      maxBytes: MAX_UPLOAD_BYTES,
    });
    if (!result.ok) {
      setUploadError(t(result.messageKey, result.params));
      return;
    }
    onUpload(group.docType, result.files);
  }

  return (
    <Card withBorder padding="lg">
      <Stack gap="md">
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <div>
            <Text fw={600}>{group.header}</Text>
            {group.subHeader ? (
              // subHeader из бэкенда несёт переносы строк тегом <br> (осмысленное форматирование —
              // абзацы, маркированные списки). Рендерим их как реальные переносы через pre-line,
              // конвертируя <br> в \n; без dangerouslySetInnerHTML — безопасно от XSS.
              <Text size="sm" c="gray.7" style={{ whiteSpace: 'pre-line' }}>
                {group.subHeader.replace(/<br\s*\/?>/gi, '\n')}
              </Text>
            ) : null}
          </div>
          {group.isMandatory ? (
            <Badge color="brand" variant="light" style={{ flexShrink: 0 }}>
              {t('app.company.accreditation.mandatory')}
            </Badge>
          ) : null}
        </Group>

        {/* Напоминание о двусторонней подписи — только для шаблона агентского договора. */}
        {group.docType === AGENCY_TEMPLATE_DOC_TYPE ? (
          <Alert
            color="wheat"
            variant="light"
            icon={<IconAlertTriangle size={18} />}
            title={t('app.company.agency.bothSignTitle')}
          >
            {t('app.company.agency.bothSignText')}
          </Alert>
        ) : null}

        {/* Пусто + есть загрузка → ниже покажется дропзона (она и есть призыв). Надпись «нет
            файлов» оставляем только когда загружать нельзя (нечем заменить пустоту). */}
        {group.fileDtoList.length === 0 ? (
          group.uploadInfo.canBeUpload ? null : (
            <Text size="sm" c="gray.6">
              {t('app.company.accreditation.noFiles')}
            </Text>
          )
        ) : (
          group.fileDtoList.map((file) => {
            // Приоритет — бейдж по итоговому docType (подписанные анкета/договор приходят с
            // docSignStatus=IsNotUsed, у которого STATUS пуст); иначе — обычный бейдж по статусу подписи.
            const signedResultBadge = SIGNED_RESULT_BADGE[file.docType];
            const badge = signedResultBadge ?? STATUS[file.docSignStatus];
            // Всплеск лучей — только когда документ ТОЛЬКО ЧТО подписан (флаг с вкладки) и это итоговый
            // SIGNED_*-документ. При обычном показе уже подписанного — тихо, без анимации.
            const showBurst = justSigned === true && signedResultBadge != null;
            return (
              <Group key={file.id} justify="space-between" wrap="nowrap">
                <div style={{ minWidth: 0 }}>
                  <Text size="sm" truncate>
                    {file.name}
                  </Text>
                  <Group gap="xs">
                    {badge ? <DocBadge color={badge.color} label={t(badge.key)} burst={showBurst} /> : null}
                    {file.isPerpetual ? (
                      <Text size="xs" c="gray.6">
                        {t('app.company.accreditation.expiry.perpetual')}
                      </Text>
                    ) : file.expiredDate ? (
                      <Text size="xs" c="gray.6">
                        {t('app.company.accreditation.validUntil', { date: file.expiredDate.slice(0, 10) })}
                      </Text>
                    ) : null}
                  </Group>
                </div>
                <Group gap={4} wrap="nowrap">
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-label={t('app.company.accreditation.download')}
                    onClick={() => onDownload(group.docType, file.id)}
                  >
                    <IconDownload size={18} />
                  </ActionIcon>
                  {group.uploadInfo.expiredDateType === 'REQUIRED' ? (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      aria-label={t('app.company.accreditation.expiry.title')}
                      onClick={() => onSetExpiry(file)}
                    >
                      <IconCalendar size={18} />
                    </ActionIcon>
                  ) : null}
                  {group.uploadInfo.canBeDeleted ? (
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      aria-label={t('app.company.banking.delete')}
                      onClick={() => onDelete(file.id)}
                    >
                      <IconTrash size={18} />
                    </ActionIcon>
                  ) : null}
                </Group>
              </Group>
            );
          })
        )}

        {/* Дропзона показывается, только пока в группе нет файла; после загрузки — файл с иконкой удаления. */}
        {group.uploadInfo.canBeUpload && group.fileDtoList.length === 0 ? (
          <UploadZone
            multiple
            loading={uploadPending}
            percent={uploadPercent}
            error={uploadError}
            maxSizeMb={MAX_UPLOAD_MB}
            accept={group.extensions.map((ext) => `.${ext.replace(/^\./, '')}`).join(',')}
            hint={group.extensions.map((ext) => ext.replace(/^\./, '').toUpperCase()).join(', ')}
            onFiles={handleFiles}
          />
        ) : null}
      </Stack>
    </Card>
  );
}
